import type { ApiSchemas } from '@rental/contracts/client'

export type BookingRow = ApiSchemas['StaffBookingRow']

/**
 * The Today board's own judgements, from the server's rows and its `now`
 * (never the device clock, which may be wrong or in another zone).
 */

/** A pick-up still waiting after its time, or a car still out after its return time. */
export function isLate(row: BookingRow, kind: 'pickup' | 'return', now: string): boolean {
  const due = Date.parse(kind === 'pickup' ? row.startsAt : row.endsAt)
  const waiting = kind === 'pickup' ? row.status === 'confirmed' : row.status === 'checked_out'
  return waiting && due < Date.parse(now)
}

/** Handed over (for a pick-up) or back (for a return): shown, but quietly. */
export function isDone(row: BookingRow, kind: 'pickup' | 'return'): boolean {
  return kind === 'pickup'
    ? row.status === 'checked_out' || row.status === 'returned' || row.status === 'closed'
    : row.status === 'returned' || row.status === 'closed'
}

/** A calendar date moved by whole days ("YYYY-MM-DD"); no time zone involved. */
export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number)
  const moved = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (day ?? 1) + days))
  return moved.toISOString().slice(0, 10)
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** A real calendar date in YYYY-MM-DD form, as the API accepts it. */
export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE.test(value) && addDays(value, 0) === value
}
